import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Stack, Checkbox, FormControlLabel, Link, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import StoreIcon from '@mui/icons-material/Store';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import AuthLayout from './AuthLayout';
import CompanySelectionModal from './CompanySelectionModal';
import { CompanyData } from '../helpers/CompanyStorage';
import * as auth from '../redux/AuthRedux';
import { login } from '../redux/AuthCRUD';
import { RootState } from '@/setup';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { LabeledField } from '@/__core/components/LabeledField';
import { saveSelectedCompany, clearSelectedCompany, getSelectedCompany } from '../helpers/CompanyStorage';
import { processCompanySelection } from '../utils/authUtils';
import { getPathWithBasename } from '@/__core/helpers/getBasename';
import { getStoredAccessToken } from '../helpers/TokenStorage';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email không đúng định dạng')
    .min(3, 'Tối thiểu 3 ký tự')
    .max(50, 'Tối đa 50 ký tự')
    .required('Email là bắt buộc'),
  password: Yup.string()
    .min(3, 'Tối thiểu 3 ký tự')
    .max(50, 'Tối đa 50 ký tự')
    .required('Mật khẩu là bắt buộc'),
})

const initialValues = {
  email: '',
  password: '',
  remember: false,
}



// Logic Component
const Login = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [hasCheckedCompanies, setHasCheckedCompanies] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [isCheckingCompanies, setIsCheckingCompanies] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const globalRoles = useSelector((state: RootState) => state.auth.globalRoles);

  // Hard guard: if token + company already exist in storage (new tab case), skip login flow entirely.
  useEffect(() => {
    const hasStoredToken = !!getStoredAccessToken();
    const storedCompany = getSelectedCompany();

    if (hasStoredToken && storedCompany) {
      dispatch(auth.actions.setSelectedCompany(storedCompany));
      window.location.replace(getPathWithBasename('/main'));
    }
  }, [dispatch]);

  // Kiểm tra khi user đã load (không cần selectedCompany)
  // Refactored to use processCompanySelection utility function
  useEffect(() => {
    const storedCompany = getSelectedCompany();
    if (user && !storedCompany && !selectedCompany && !shouldShowModal && !hasCheckedCompanies) {
      const checkCompaniesForModal = async () => {
        try {
          setHasCheckedCompanies(true);

          // If company context already exists in storage (e.g. new tab), restore it and skip modal flow.
          const storedCompany = getSelectedCompany();
          if (storedCompany) {
            dispatch(auth.actions.setSelectedCompany(storedCompany));
            setShouldShowModal(true);
            navigate('/main');
            return;
          }

          // Derive remember flag from persisted token state.
          // This avoids overriding `remember_company` to false on tab reload/auto-login.
          const remember = !!localStorage.getItem('rememberToken');
          const result = await processCompanySelection(dispatch, remember);
          
          // QUY LUẬT 2: Single Company
          if (result.type === 'single') {
            setShouldShowModal(true);
            navigate('/main');
          }
          else if (result.type === 'multiple') {
            if (result.companies) {
              setCompanies(result.companies);
            }
            setShowCompanySelection(true);
            setShouldShowModal(true);
          }
          else if (result.type === 'none') {
            setShouldShowModal(true);
            navigate('/main');
          }
        } catch (error) {
          console.error('Error checking companies for modal:', error);
          setShouldShowModal(true);
        }
      };
      
      checkCompaniesForModal();
    }
  }, [user, selectedCompany, shouldShowModal, hasCheckedCompanies, dispatch, navigate]);

  // Redirect khi user đã được load và đã chọn company
  useEffect(() => {
    // console.log('🔄 Redirect useEffect triggered:', {
    //   user: !!user,
    //   selectedCompany: !!selectedCompany,
    //   showCompanySelection,
    //   shouldShowModal,
    //   hasCheckedCompanies,
    //   shouldRedirect: user && selectedCompany && !showCompanySelection && shouldShowModal && hasCheckedCompanies
    // });
    
    // Chỉ redirect nếu:
    // 1. User đã load
    // 2. Đã có selectedCompany (từ storage hoặc từ login)
    // 3. Không đang hiển thị modal chọn company
    // 4. Đã kiểm tra xem có cần modal không
    // 5. Modal không đang được hiển thị
    // 6. Đã kiểm tra companies
    if (user && selectedCompany && !showCompanySelection && shouldShowModal && hasCheckedCompanies) {
      // console.log('🚀 Redirecting to /main');
      navigate('/main');
    }
  }, [user, selectedCompany, showCompanySelection, shouldShowModal, hasCheckedCompanies, navigate]);

  // Block redirect khi modal đang hiển thị
  useEffect(() => {
    if (showCompanySelection) {
      // console.log('🚫 Modal is showing, blocking any redirects');
    }
  }, [showCompanySelection]);

  // Reset form sau khi redirect thành công
  useEffect(() => {
    if (user && selectedCompany && !showCompanySelection && shouldShowModal && hasCheckedCompanies) {
      // Reset form sau khi đã redirect thành công
      setTimeout(() => {
        formik.resetForm();
      }, 100); // Delay nhỏ để đảm bảo redirect đã xảy ra
    }
  }, [user, selectedCompany, showCompanySelection, shouldShowModal, hasCheckedCompanies]);

  // Function to handle company selection
  const handleCompanySelection = async (company: CompanyData) => {
    try {
      // console.log('🎯 Company selected:', company.company_name);

      const companyRoles = Array.isArray(company.roles) ? company.roles : [];
      const safeGlobalRoles = Array.isArray(globalRoles) ? globalRoles : [];

      // Global users may receive companies without role bindings.
      // Merge global roles into selected company to keep permissions available in storage/context.
      const mergedRolesRaw = [...companyRoles, ...safeGlobalRoles];
      const uniqueRolesMap = new Map<string, any>();

      mergedRolesRaw.forEach((role: any) => {
        const roleKey = String(role?.role_id || `${role?.role_name || 'unknown'}:${role?.role_level || 'company'}`);
        if (!uniqueRolesMap.has(roleKey)) {
          uniqueRolesMap.set(roleKey, {
            role_id: role?.role_id || roleKey,
            role_name: role?.role_name || 'unknown',
            role_display_name: role?.role_display_name || role?.role_name || 'Unknown Role',
            role_level: role?.role_level || 'company',
            permissions: role?.permissions || {},
            is_primary: Boolean(role?.is_primary),
            created_at: role?.created_at || new Date().toISOString()
          });
        }
      });

      const mergedRoles = Array.from(uniqueRolesMap.values());
      const companyWithMergedRoles: CompanyData = {
        ...company,
        roles: mergedRoles
      };
      
      // Save selected company to storage
      saveSelectedCompany(companyWithMergedRoles, formik.values.remember);
      
        // Dispatch to Redux store
        dispatch(auth.actions.setSelectedCompany({
          company_id: company.company_id,
          company_name: company.company_name,
          company_code: company.company_code,
          company_type: company.company_type,
          roles: mergedRoles.map((role: any) => ({
            role_id: role.role_id,
            role_name: role.role_name,
            role_display_name: role.role_display_name,
            role_level: role.role_level || 'company',
            permissions: role.permissions || {},
            is_primary: role.is_primary,
            created_at: role.created_at
          })),
          selected_at: new Date().toISOString()
      }));
      
      // Close modal
      setShowCompanySelection(false);
      setShouldShowModal(false);
      
      // Navigate to main page
      // console.log('🚀 Redirecting after company selection');
      navigate('/main');
    } catch (error) {
      console.error('Error selecting company:', error);
      setErrorMessage('Có lỗi xảy ra khi chọn công ty');
    }
  };

  // Function to check companies after login
  // Refactored to use processCompanySelection utility function
  const checkUserCompanies = async () => {
    if (isCheckingCompanies) {
      console.log('⏳ Already checking companies, skipping...');
      return;
    }
    
    try {
      setIsCheckingCompanies(true);
      setLoadingCompanies(true);
      console.log('📡 Processing company selection...');
      
      // Use centralized company selection logic
      const result = await processCompanySelection(dispatch, formik.values.remember);
      
      console.log('📊 Company selection result:', result.type);
      
      // QUY LUẬT 2: Single Company
      if (result.type === 'single') {
        if (result.companies) {
          setCompanies(result.companies);
        }
        console.log('🚀 Redirecting to /main with single company');
        navigate('/main');
      }
      else if (result.type === 'multiple') {
        if (result.companies) {
          setCompanies(result.companies);
          setShowCompanySelection(true);
          setShouldShowModal(true);
        }
      }
      else if (result.type === 'none') {
        console.log('🚀 No company found, redirecting to /main');
        navigate('/main');
      }
      
    } catch (error) {
      console.error('Error processing company selection:', error);
      setErrorMessage('Có lỗi xảy ra khi tải danh sách công ty');
    } finally {
      setIsCheckingCompanies(false);
      setLoadingCompanies(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);
        setIsLoggingIn(true);
        setErrorMessage(null);     

        // Always clear previous company context before a new login flow.
        clearSelectedCompany();
        dispatch(auth.actions.clearSelectedCompany());
        
        const response = await login(values.email, values.password);
        
        // console.log('✅ Login successful:', response.data);
        
        // Xử lý "Duy trì đăng nhập"
        const accessToken = response.data.access;
        
        if (values.remember) {
          // Lưu token vào localStorage (không bao giờ hết hạn)
          localStorage.setItem('rememberToken', accessToken);
          localStorage.setItem('rememberUser', values.email);
        } else {
          // Lưu token vào sessionStorage (sẽ mất khi đóng browser)
          sessionStorage.setItem('sessionToken', accessToken);
          sessionStorage.setItem('sessionUser', values.email);
        }
        
        dispatch(auth.actions.login(accessToken));
        
        // Check user companies after successful login
        console.log('🔍 Starting checkUserCompanies...');
        await checkUserCompanies();
        console.log('✅ checkUserCompanies completed');
        
        // KHÔNG reset form khi login thành công để tránh flash của input fields
        // Form sẽ được giữ nguyên với loading state cho đến khi redirect
        
      } catch (error: any) {
        console.error('Login error:', error);
        const errorMessage = formatErrorForSnackbar(error);
        setErrorMessage(errorMessage || 'Thông tin đăng nhập không chính xác');
        // Chỉ reset form khi có lỗi
        resetForm();
      } finally {
        setSubmitting(false);
        setIsLoggingIn(false);
      }
    },
  });

  return (
    <AuthLayout>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={3}>
          <Box sx={{ mb: 2 }}>
          <LabeledField label="Email" labelWidth={60} >
            <TextField
              {...formik.getFieldProps('email')}
              fullWidth
              required
              autoFocus
              size="small"
              error={formik.touched.email && Boolean(formik.errors.email)}
            />
          </LabeledField>
              </Box>
              <Box sx={{ mb: 2 }}>
          <LabeledField label="Mật khẩu" labelWidth={60}>
            <TextField
              type="password"
              {...formik.getFieldProps('password')}
              fullWidth
              required
              size="small"
              error={formik.touched.password && Boolean(formik.errors.password)}
            />
          </LabeledField>
          </Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" py={2} px={1}>
            <FormControlLabel
              control={
                <Checkbox
                  name="remember"
                  checked={Boolean(formik.values.remember)}
                  onChange={(event) => formik.setFieldValue('remember', event.target.checked)}
                  color="primary"
                />
              }
              label="Duy trì đăng nhập"
            />
            <Link
              component={RouterLink}
              to="/auth/forgot-password"
              underline="hover"
              color="primary"
              fontSize={14}
            >
              Quên mật khẩu?
            </Link>
          </Stack>
          
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              startIcon={isLoggingIn ? <CircularProgress size={20} color="inherit" /> : <StoreIcon />}
              sx={{ fontWeight: 'bold', fontSize: 16 }}
              size="small"
              disabled={isLoggingIn || !formik.isValid}
            >
              {isLoggingIn ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </Button>
            
          </Stack>
        </Stack>
      </form>
      
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Company Selection Modal */}
      <CompanySelectionModal
        open={showCompanySelection}
        onClose={() => setShowCompanySelection(false)}
        onSelectCompany={handleCompanySelection}
        companies={companies}
        loading={loadingCompanies}
        disableClose={true}
      />
    </AuthLayout>
  );
};

export default Login; 